import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MapPin, ArrowClockwise, DownloadSimple, Warning, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";

const CURRENT_YEAR = new Date().getFullYear();
const QUARTERS = [
  { key: 1, label: "Q1 (Jan–Mar)" },
  { key: 2, label: "Q2 (Apr–Jun)" },
  { key: 3, label: "Q3 (Jul–Sep)" },
  { key: 4, label: "Q4 (Oct–Dec)" },
  { key: null, label: "Full year" },
];
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

function fmtInt(n) { return Math.round(n || 0).toLocaleString(); }
function fmtUSD(n) { return `$${Math.round(n || 0).toLocaleString()}`; }

/** Simple horizontal bar rendered as a % width fill. */
function MilesBar({ pct }) {
  return (
    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
      <div
        className="h-full bg-[#FF5A1F]/80 rounded-full"
        style={{ width: `${Math.min(100, Math.max(2, pct || 0))}%` }}
      />
    </div>
  );
}

function downloadCSV(data) {
  const header = ["State", "Loaded miles", "Load touches", "Revenue share ($)", "% of total"];
  const rows = data.states.map((s) => [s.state, s.miles, s.load_count, s.revenue, s.pct_of_total]);
  const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `state-mileage-${data.year}-${data.quarter || "full"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function StateMileageReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (quarter !== null) params.set("quarter", quarter);
      const { data } = await client.get(`/reports/state-mileage?${params.toString()}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load state mileage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  const hasData = useMemo(() => (data?.states?.length || 0) > 0, [data]);

  return (
    <div className="space-y-6" data-testid="state-mileage-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">IFTA · QUARTERLY MILEAGE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            State mileage breakdown
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            A quick-look breakdown of how many delivered miles you ran per state each quarter.
            Perfect for eyeballing your IFTA quarterly filing before your accountant runs the
            real numbers.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={loading}
            data-testid="state-mileage-year"
            className="rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-white focus:border-[#FF5A1F] outline-none"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex items-center gap-1 rounded-full border border-white/10 p-0.5">
            {QUARTERS.map((q) => (
              <button
                key={q.key === null ? "full" : q.key}
                onClick={() => setQuarter(q.key)}
                disabled={loading}
                data-testid={`state-mileage-q-${q.key === null ? "full" : q.key}`}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  quarter === q.key
                    ? "bg-white/10 text-white"
                    : "text-neutral-400 hover:text-white"
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="state-mileage-refresh"
            className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-white/25 transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {hasData && (
            <button
              onClick={() => downloadCSV(data)}
              data-testid="state-mileage-csv"
              className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
            >
              <DownloadSimple size={12} weight="bold" /> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Approximation disclaimer */}
      <div
        className="rounded-lg border border-amber-500/25 bg-amber-500/[0.04] p-3 flex items-start gap-2.5"
        data-testid="state-mileage-disclaimer"
      >
        <Info size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-[12px] text-amber-100/85 leading-relaxed">
          <b className="text-amber-200">Approximation:</b> miles are split 50/50 between each load's
          origin state and destination state. Use this to spot trends and prep for your
          accountant — <b>not</b> for the tax filing itself (that requires per-state routing miles
          from a certified engine).
        </div>
      </div>

      {/* Summary bar */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCell label={data.label || "Period"} value={data.label} />
          <SummaryCell label="Total loaded miles" value={fmtInt(data.total_miles)} />
          <SummaryCell label="Delivered loads" value={data.total_loads} />
          <SummaryCell label="Total revenue" value={fmtUSD(data.total_revenue)} />
        </div>
      )}

      {/* State table */}
      {loading ? (
        <div className="rounded-lg border border-white/5 p-12 text-center text-sm text-neutral-500">
          Loading…
        </div>
      ) : !hasData ? (
        <div className="rounded-lg border border-dashed border-white/10 p-12 text-center">
          <MapPin size={20} className="mx-auto mb-2 text-white/40" />
          <div className="text-sm text-white/60">
            No delivered loads for {data?.label || "this period"}.
          </div>
          <div className="text-[11px] text-white/40 mt-1">
            Ship a few loads with parsed origin/destination states and they'll show up here.
          </div>
          {data?.unmapped_loads > 0 && (
            <div className="text-[11px] text-amber-300 mt-2">
              {data.unmapped_loads} load{data.unmapped_loads !== 1 ? "s" : ""} skipped —
              origin/destination state fields were empty. Try recalculating miles on the Loads page.
            </div>
          )}
        </div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="state-mileage-table">
          <div className="grid grid-cols-[60px_1fr_130px_100px_130px] gap-3 px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/5 font-semibold">
            <div>State</div>
            <div>Distribution</div>
            <div className="text-right">Miles</div>
            <div className="text-right">Loads</div>
            <div className="text-right">Revenue share</div>
          </div>
          {data.states.map((s) => (
            <div
              key={s.state}
              className="grid grid-cols-[60px_1fr_130px_100px_130px] gap-3 items-center px-4 py-3 border-t border-white/5"
              data-testid={`state-mileage-row-${s.state}`}
            >
              <div className="text-white font-semibold tabular-nums">{s.state}</div>
              <div>
                <MilesBar pct={s.pct_of_total} />
                <div className="text-[10px] text-neutral-500 mt-1">{s.pct_of_total}% of total</div>
              </div>
              <div className="text-right font-mono text-sm text-white tabular-nums">
                {fmtInt(s.miles)} mi
              </div>
              <div className="text-right text-sm text-neutral-300 tabular-nums">
                {s.load_count}
              </div>
              <div className="text-right font-mono text-sm text-neutral-300 tabular-nums">
                {fmtUSD(s.revenue)}
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.unmapped_loads > 0 && hasData && (
        <div className="text-[11px] text-amber-300/80 flex items-center gap-1.5">
          <Warning size={11} weight="fill" />
          {data.unmapped_loads} load{data.unmapped_loads !== 1 ? "s" : ""} skipped — origin/destination
          state fields were empty. Recalc miles on the Loads page to include them.
        </div>
      )}
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
        {label}
      </div>
      <div className="text-xl font-semibold text-white tabular-nums">{value}</div>
    </div>
  );
}
